import { z } from 'zod';

/**
 * Contract for GET admin/users.
 *
 * The envelope (pagination, totals, users array) must be valid or the whole response
 * is rejected. Rows are checked field by field instead, so one malformed user only
 * blanks its bad cells rather than breaking the table (see `parseUserRow`).
 */

const nullableString = z.string().nullable();

/** Each field's schema. Optional fields are not sent by the backend yet. */
export const USER_FIELDS = {
  id: z.union([z.number(), z.string()]),
  userId: z.string().min(1),
  name: nullableString,
  email: nullableString,
  phoneNumber: nullableString,
  isActive: z.boolean().nullable(),
  scansMade: z.number().int().nonnegative().nullable(),
  fuelType: nullableString,
  city: nullableString,
  country: nullableString,
  createdAt: nullableString,
  // Not in the API yet; the UI lights these up once the backend adds them
  tier: nullableString.optional(),
  couponsEarned: z.number().int().nonnegative().nullable().optional(),
  scansToNextTier: z.number().int().nonnegative().nullable().optional(),
  emailVerified: z.boolean().nullable().optional(),
};

const REQUIRED_FIELDS = ['id', 'userId'];

export const paginationSchema = z.object({
  page: z.number().int().min(1),
  limit: z.number().int().min(1),
  totalItems: z.number().int().nonnegative(),
  totalPages: z.number().int().nonnegative(),
  hasNextPage: z.boolean(),
  hasPrevPage: z.boolean(),
});

export const usersEnvelopeSchema = z.object({
  users: z.array(z.unknown()),
  pagination: paginationSchema,
  totalRegistered: z.number().int().nonnegative(),
});

/**
 * @typedef {object} UserDto  Raw row from the API, after per-field validation.
 *   Invalid fields are replaced with `null`.
 * @property {number|string|null} id
 * @property {string|null} userId
 * @property {string|null} name
 * @property {string|null} email
 * @property {string|null} phoneNumber
 * @property {boolean|null} isActive
 * @property {number|null} scansMade
 * @property {string|null} fuelType
 * @property {string|null} city
 * @property {string|null} createdAt
 * @property {string|null} [tier]
 * @property {number|null} [couponsEarned]
 * @property {number|null} [scansToNextTier]
 * @property {boolean|null} [emailVerified]
 */

/**
 * Validates one raw row. Returns `{ row, invalidFields }`; bad or missing fields
 * become `null` so the UI can show "—" for just those cells.
 * @returns {{ row: UserDto, invalidFields: string[] }}
 */
export function parseUserRow(raw) {
  const source = raw && typeof raw === 'object' ? raw : {};
  const row = {};
  const invalidFields = [];
  for (const [field, schema] of Object.entries(USER_FIELDS)) {
    // An absent field just has no value; only identity fields must always be present
    if (source[field] === undefined && !REQUIRED_FIELDS.includes(field)) {
      row[field] = null;
      continue;
    }
    const result = schema.safeParse(source[field]);
    if (result.success) {
      row[field] = result.data ?? null;
    } else {
      row[field] = null;
      invalidFields.push(field);
    }
  }
  return { row, invalidFields };
}
